import RequiredMark from './RequiredMark';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import { toYmd, formatDisplayYmd, getDecadeRange } from '../lib/calendarYmd';
import { ymdInTz } from '../lib/datetime';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import {
    tableToolbarDatePickerTriggerClass,
    tableToolbarControlOpenClass,
    tableToolbarPlaceholderTextClass,
    tableToolbarValueTextClass,
    tableToolbarIconClass,
    tableToolbarIconOpenClass,
} from '../lib/tableClasses';

const DEFAULT_MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DEFAULT_DAY_NAMES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const DEFAULT_PLACEHOLDER = 'Seleccionar fecha';

/** Measure the inner input box (the visible field), not the wrapper div. */
const measureInputBox = (el: HTMLElement): DOMRect =>
    (el.querySelector('.input-container-box') ?? el).getBoundingClientRect();

export interface CustomDatePickerProps {
    label: string;
    /** Required field: red asterisk after the label (Standards §14.7). */
    markRequired?: boolean;
    selectedDate: string; // YYYY-MM-DD
    onChange: (date: string) => void;
    required?: boolean;
    align?: 'left' | 'right';
    /** Use 'tableToolbar' when placed inside tableToolbarClass for matching surface. */
    variant?: 'default' | 'tableToolbar';
    /** Localization overrides — defaults to Spanish. Pass arrays of length 12/7. */
    monthNames?: string[];
    dayNames?: string[];
    placeholder?: string;
    /** How the chosen day reads in the field. Default `dd/mm/yyyy`; a caller
     *  that has the person's language passes its own (e.g. «1 oct 2026»
     *  inside a sentence). */
    formatDisplay?: (ymd: string) => string;
    /** The day marked as today (`YYYY-MM-DD`). Default: the viewer's own day,
     *  never the UTC one (which in Madrid is yesterday until 01:00/02:00). */
    today?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
    label,
    markRequired = false,
    selectedDate,
    onChange,
    align = 'left',
    variant = 'default',
    monthNames = DEFAULT_MONTH_NAMES,
    dayNames = DEFAULT_DAY_NAMES,
    placeholder = DEFAULT_PLACEHOLDER,
    formatDisplay = formatDisplayYmd,
    today,
}) => {
    const showLabel = Boolean(label?.trim());
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date(selectedDate || new Date()));
    const [pickerView, setPickerView] = useState<'days' | 'months' | 'years'>('days');
    const datePickerRef = useRef<HTMLDivElement>(null);
    // Anchored position with vertical flip (opens upward when clipped at the
    // bottom of the viewport) + horizontal clamp. Shared with all pickers.
    const pos = useAnchoredPosition(datePickerRef, isOpen, {
        estimatedHeight: 360,
        estimatedWidth: 288,
        align,
        gap: 4,
        measure: measureInputBox,
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
                const target = event.target as HTMLElement;
                if (!target.closest('.datepicker-portal-content')) {
                    setIsOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        const initialDate = selectedDate ? new Date(selectedDate) : new Date();
        setViewDate(isNaN(initialDate.getTime()) ? new Date() : initialDate);
    }, [selectedDate]);

    const togglePicker = () => {
        if (!isOpen) {
            setPickerView('days');
        }
        setIsOpen(!isOpen);
    };

    const handlePrevMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const handleNextMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    const handlePrevYear = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() - 1)));
    const handleNextYear = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() + 1)));
    const handlePrevDecade = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() - 10)));
    const handleNextDecade = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() + 10)));

    const handleDateClick = (year: number, month: number, day: number) => {
        onChange(toYmd(year, month, day));
        setIsOpen(false);
    };
    const handleMonthClick = (monthIndex: number) => {
        setViewDate(prev => new Date(prev.setMonth(monthIndex)));
        setPickerView('days');
    };
    const handleYearClick = (year: number) => {
        setViewDate(prev => new Date(prev.setFullYear(year)));
        setPickerView('months');
    };

    const renderDaysView = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const prevMonthDate = new Date(year, month, 0);
        const daysInPrevMonth = prevMonthDate.getDate();
        const startDayOfWeek = (firstDayOfMonth === 0) ? 6 : firstDayOfMonth - 1;
        const days: React.ReactNode[] = [];

        for (let i = 0; i < startDayOfWeek; i++) {
            const day = daysInPrevMonth - startDayOfWeek + 1 + i;
            days.push(<div key={`pad-start-${i}`} onClick={() => handleDateClick(prevMonthDate.getFullYear(), prevMonthDate.getMonth(), day)} className="text-center p-1.5 text-slate-300 text-sm cursor-pointer rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">{day}</div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const currentDateStr = toYmd(year, month, day);
            const isSelected = selectedDate === currentDateStr;
            const isToday = (today ?? ymdInTz(new Date())) === currentDateStr;
            const classes = `text-center p-1.5 rounded-sm cursor-pointer transition-colors text-sm ${isSelected
                ? 'bg-primary text-white font-bold shadow-sm'
                : isToday
                    ? 'text-primary font-bold bg-primary/5 ring-1 ring-inset ring-primary/20'
                    : 'text-text-main dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`;
            days.push(<div key={day} onClick={() => handleDateClick(year, month, day)} className={classes}>{day}</div>);
        }

        const grid_size = 42;
        const nextMonthDate = new Date(year, month + 1, 1);
        for (let i = 1; i <= grid_size - days.length; i++) {
            days.push(<div key={`pad-end-${i}`} onClick={() => handleDateClick(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), i)} className="text-center p-1.5 text-slate-300 text-sm cursor-pointer rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">{i}</div>);
        }

        return (
            <>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-text-sub dark:text-slate-400 uppercase tracking-wider mb-2">
                    {dayNames.map(day => <div key={day}>{day}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">{days}</div>
            </>
        );
    };

    const renderMonthsView = () => {
        const selectedMonth = selectedDate && new Date(selectedDate).getFullYear() === viewDate.getFullYear() ? new Date(selectedDate).getMonth() : -1;
        return (
            <div className="grid grid-cols-3 gap-2">
                {monthNames.map((month, index) => (
                    <button key={month} type="button" onClick={() => handleMonthClick(index)} className={`p-2 rounded-md text-sm transition-colors ${index === selectedMonth ? 'bg-primary text-white font-bold shadow-sm' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                        {month.substring(0, 3)}
                    </button>
                ))}
            </div>
        );
    };

    const renderYearsView = () => {
        const { start, end } = getDecadeRange(viewDate.getFullYear());
        const years = Array.from({ length: end - start + 3 }, (_, i) => start - 1 + i);
        const selectedYear = selectedDate ? new Date(selectedDate).getFullYear() : -1;
        return (
            <div className="grid grid-cols-4 gap-2">
                {years.map(year => (
                    <button key={year} type="button" onClick={() => handleYearClick(year)} className={`p-2 rounded-md text-sm transition-colors ${year === selectedYear ? 'bg-primary text-white font-bold shadow-sm' : 'hover:bg-slate-50 dark:hover:bg-slate-800'} ${year < start || year > end ? 'text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>
                        {year}
                    </button>
                ))}
            </div>
        );
    };

    const renderHeader = () => {
        const year = viewDate.getFullYear();
        const { start, end } = getDecadeRange(year);

        const NavButton: React.FC<{ onClick: () => void; icon: IconName; label: string }> = ({ onClick, icon, label }) => (
            <button type="button" onClick={onClick} aria-label={label} className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <Icon name={icon} className="!text-[18px]" />
            </button>
        );

        if (pickerView === 'years') return (
            <div className="flex items-center justify-between mb-3 px-1">
                <NavButton onClick={handlePrevDecade} icon="chevron_left" label="Década anterior" />
                <span className="font-bold text-sm text-text-main dark:text-white">{start} - {end}</span>
                <NavButton onClick={handleNextDecade} icon="chevron_right" label="Década siguiente" />
            </div>
        );
        if (pickerView === 'months') return (
            <div className="flex items-center justify-between mb-3 px-1">
                <NavButton onClick={handlePrevYear} icon="chevron_left" label="Año anterior" />
                <button type="button" onClick={() => setPickerView('years')} className="font-bold text-sm text-text-main dark:text-white hover:text-primary transition-colors">{year}</button>
                <NavButton onClick={handleNextYear} icon="chevron_right" label="Año siguiente" />
            </div>
        );
        return (
            <div className="flex items-center justify-between mb-3 px-1">
                <NavButton onClick={handlePrevMonth} icon="chevron_left" label="Mes anterior" />
                <button type="button" onClick={() => setPickerView('months')} className="font-bold text-sm text-text-main dark:text-white hover:text-primary transition-colors">
                    {monthNames[viewDate.getMonth()]} <span className="text-xs ml-1 opacity-80">{year}</span>
                </button>
                <NavButton onClick={handleNextMonth} icon="chevron_right" label="Mes siguiente" />
            </div>
        );
    };

    const toolbarTriggerClass = [
        'input-container-box',
        tableToolbarDatePickerTriggerClass,
        isOpen ? tableToolbarControlOpenClass : '',
    ].filter(Boolean).join(' ');

    const defaultTriggerClass = [
        'input-container-box relative w-full h-10 flex items-center justify-between cursor-pointer rounded-md border px-3 transition-all shadow-sm',
        'bg-transparent',
        isOpen
            ? 'border-primary ring-1 ring-primary'
            : 'border-border-strong dark:border-border-strong-dark active:opacity-90',
    ].join(' ');

    return (
        <div
            className={`relative w-full min-w-0 flex flex-col justify-center ${variant === 'tableToolbar' && !showLabel ? 'h-10' : ''} ${showLabel && variant !== 'tableToolbar' ? 'pt-2' : ''}`}
            ref={datePickerRef}
        >
            {showLabel && variant !== 'tableToolbar' ? (
                <span className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-10 pointer-events-none">{label}{markRequired && <> <RequiredMark /></>}</span>
            ) : null}
            <div
                onClick={togglePicker}
                className={variant === 'tableToolbar' ? toolbarTriggerClass : defaultTriggerClass}
            >
                <span
                    className={`min-w-0 truncate tabular-nums ${
                        selectedDate ? tableToolbarValueTextClass : tableToolbarPlaceholderTextClass
                    }`}
                >
                    {(selectedDate && formatDisplay(selectedDate)) || placeholder}
                </span>
                <Icon name="calendar_month" className={`${tableToolbarIconClass} ${isOpen ? tableToolbarIconOpenClass : ''}`} />
            </div>
            {isOpen && ReactDOM.createPortal(
                <div
                    className={`datepicker-portal-content fixed z-[9999] w-72 bg-white dark:bg-surface-dark shadow-xl shadow-slate-200/50 dark:shadow-black/50 rounded-lg border border-border-subtle dark:border-border-subtle-dark p-3 animate-in fade-in zoom-in-95 duration-100 ${pos?.flip ? 'origin-bottom' : 'origin-top'}`}
                    style={{ top: pos?.top, bottom: pos?.bottom, left: pos?.left, visibility: pos ? 'visible' : 'hidden' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderHeader()}
                    {pickerView === 'days' && renderDaysView()}
                    {pickerView === 'months' && renderMonthsView()}
                    {pickerView === 'years' && renderYearsView()}
                </div>,
                document.body
            )}
        </div>
    );
};

export default CustomDatePicker;
